/**
 * Stripe Payment Form Component
 *
 * Secure payment form using Stripe Elements
 */

import { useMemo, useState, useEffect } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { paymentService } from '@/services/paymentService';
import type { CreatePaymentIntentData } from '@/types/payment';

interface StripePaymentFormProps {
  amount: number;
  currency?: string;
  donationId?: number;
  membershipApplicationId?: number;
  customerEmail: string;
  customerName?: string;
  description?: string;
  onSuccess?: (paymentId: string) => void;
  onError?: (error: string) => void;
  metadata?: Record<string, any>;
}

const PaymentFormContent = ({
  amount,
  currency = 'USD',
  donationId,
  membershipApplicationId,
  customerEmail,
  customerName,
  description,
  onSuccess,
  onError,
  metadata
}: StripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Create payment intent on mount
  useEffect(() => {
    const createIntent = async () => {
      try {
        const result = await paymentService.createPaymentIntent({
          amount,
          currency,
          donationId,
          membershipApplicationId,
          customerEmail,
          customerName,
          description,
          metadata
        });

        if (result.success && result.data) {
          setClientSecret(result.data.clientSecret);
        } else {
          setError(result.error?.getUserMessage() || 'Failed to initialize payment');
          onError?.(result.error?.getUserMessage() || 'Failed to initialize payment');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment';
        setError(errorMessage);
        onError?.(errorMessage);
      }
    };

    if (amount > 0) {
      createIntent();
    }
  }, [amount, currency, donationId, membershipApplicationId, customerEmail, customerName, description, metadata, onError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setIsProcessing(false);
      return;
    }

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: customerName || customerEmail,
            email: customerEmail,
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        toast.error(stripeError.message || 'Payment failed');
        onError?.(stripeError.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Save payment to database
        const saveResult = await paymentService.savePayment({
          donationId,
          membershipApplicationId,
          paymentGateway: 'stripe',
          gatewayPaymentId: paymentIntent.id,
          gatewayCustomerId: paymentIntent.customer as string || undefined,
          amount,
          currency,
          paymentMethodType: paymentIntent.payment_method_types[0] || 'card',
          paymentStatus: 'succeeded',
          transactionId: paymentIntent.id,
          receiptUrl: paymentIntent.receipt_url || undefined,
          processedAt: new Date().toISOString(),
        });

        if (saveResult.success) {
          toast.success('Payment processed successfully!');
          onSuccess?.(paymentIntent.id);
        } else {
          // Payment succeeded but failed to save - this is a problem
          console.error('Payment succeeded but failed to save:', saveResult.error);
          setError('Payment processed but failed to record. Please contact support.');
          onError?.('Payment processed but failed to record');
        }
      } else {
        setError('Payment was not completed');
        onError?.('Payment was not completed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment processing failed';
      setError(errorMessage);
      toast.error(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  if (!clientSecret) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">Initializing payment...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="card-element">Card Details</Label>
        <div className="p-4 border rounded-lg bg-background">
          <CardElement id="card-element" options={cardElementOptions} />
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold">Amount:</span> {currency} ${amount.toFixed(2)}
        </div>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="min-w-[120px]"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="mr-2 h-4 w-4" />
              Pay ${amount.toFixed(2)}
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Your payment is secure and encrypted. We never store your card details.
      </p>
    </form>
  );
};

export const StripePaymentForm = (props: StripePaymentFormProps) => {
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  // IMPORTANT: only call loadStripe() when a real key exists.
  // Calling it with an empty string throws in production and can break unrelated pages.
  const stripePromise = useMemo(() => {
    if (!publishableKey) return null;
    return loadStripe(publishableKey);
  }, [publishableKey]);

  const options: StripeElementsOptions = {
    appearance: {
      theme: 'stripe',
    },
  };

  if (!publishableKey || !stripePromise) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              Stripe is not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY environment variable.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentFormContent {...props} />
    </Elements>
  );
};
